/**
 * Conflict Detection Algorithms
 *
 * This module provides sophisticated conflict detection for versioning operations.
 * It implements three-way merge algorithms and conflict resolution strategies.
 */

import {
  ProjectSnapshot,
  PageSnapshot,
  ProjectNavigationSettings,
  ProjectSettings,
  VersionChange,
  Component,
  VersionConflict,
} from "@/types";
import { generateId } from "@/utils";

function getFieldValue(source: object, key: string): unknown {
  return Reflect.get(source, key);
}

export interface ConflictDetectionResult {
  hasConflicts: boolean;
  conflicts: VersionConflict[];
  resolution: "automatic" | "manual" | "impossible";
  confidence: number; // 0-100
}

export interface ConflictContext {
  baseSnapshot: ProjectSnapshot;
  sourceSnapshot: ProjectSnapshot;
  targetSnapshot: ProjectSnapshot;
  sourceChanges: VersionChange[];
  targetChanges: VersionChange[];
}

/**
 * Main conflict detection engine
 */
export class ConflictDetector {
  private static instance: ConflictDetector;

  static getInstance(): ConflictDetector {
    if (!ConflictDetector.instance) {
      ConflictDetector.instance = new ConflictDetector();
    }
    return ConflictDetector.instance;
  }

  /**
   * Detect conflicts between two snapshots based on a common base
   */
  async detectConflicts(
    context: ConflictContext,
  ): Promise<ConflictDetectionResult> {
    const conflicts: VersionConflict[] = [];

    // Component-level conflict detection
    const componentConflicts = this.detectComponentConflicts(context);
    conflicts.push(...componentConflicts);

    // Page-level conflict detection
    const pageConflicts = this.detectPageConflicts(context);
    conflicts.push(...pageConflicts);

    // Settings conflict detection
    const settingsConflicts = this.detectSettingsConflicts(context);
    conflicts.push(...settingsConflicts);

    // Analyze resolution strategy
    const resolution = this.analyzeResolutionStrategy(conflicts);
    const confidence = this.calculateConfidence(conflicts);

    return {
      hasConflicts: conflicts.length > 0,
      conflicts,
      resolution,
      confidence,
    };
  }

  /**
   * Detect conflicts in component changes
   */
  private detectComponentConflicts(
    context: ConflictContext,
  ): VersionConflict[] {
    const conflicts: VersionConflict[] = [];
    const { baseSnapshot, sourceSnapshot, targetSnapshot } = context;

    // Build component maps for easy lookup
    const baseComponents = this.buildComponentMap(baseSnapshot);
    const sourceComponents = this.buildComponentMap(sourceSnapshot);
    const targetComponents = this.buildComponentMap(targetSnapshot);

    // Get all component IDs across all snapshots
    const allComponentIds = new Set([
      ...Object.keys(baseComponents),
      ...Object.keys(sourceComponents),
      ...Object.keys(targetComponents),
    ]);

    for (const componentId of allComponentIds) {
      const baseComponent = baseComponents[componentId];
      const sourceComponent = sourceComponents[componentId];
      const targetComponent = targetComponents[componentId];

      const conflict = this.analyzeComponentConflict(
        componentId,
        baseComponent,
        sourceComponent,
        targetComponent,
      );

      if (conflict) {
        conflicts.push(conflict);
      }
    }

    return conflicts;
  }

  /**
   * Analyze potential conflict for a single component
   */
  private analyzeComponentConflict(
    componentId: string,
    base?: Component,
    source?: Component,
    target?: Component,
  ): VersionConflict | null {
    // Case 1: Component deleted in both branches
    if (!source && !target && base) {
      return null; // No conflict, both deleted
    }

    // Case 2: Component deleted in one branch, modified in another
    if (base && !source && target && this.isComponentModified(base, target)) {
      return {
        id: generateId(),
        type: "component_deleted",
        target: "component",
        targetId: componentId,
        pageId: target.pageId,
        baseValue: base,
        sourceValue: undefined,
        targetValue: target,
        description: `Component ${componentId} deleted in source but modified in target`,
      };
    }

    if (base && source && !target && this.isComponentModified(base, source)) {
      return {
        id: generateId(),
        type: "component_deleted",
        target: "component",
        targetId: componentId,
        pageId: source.pageId,
        baseValue: base,
        sourceValue: source,
        targetValue: undefined,
        description: `Component ${componentId} modified in source but deleted in target`,
      };
    }

    // Case 3: Component modified in both branches
    if (base && source && target) {
      const sourceModified = this.isComponentModified(base, source);
      const targetModified = this.isComponentModified(base, target);

      if (sourceModified && targetModified) {
        // Check if modifications are compatible
        const compatibilityResult = this.checkComponentCompatibility(
          base,
          source,
          target,
        );

        if (!compatibilityResult.compatible) {
          return {
            id: generateId(),
            type: "component_modified",
            target: "component",
            targetId: componentId,
            pageId: source.pageId,
            field: compatibilityResult.conflictingField,
            baseValue: base,
            sourceValue: source,
            targetValue: target,
            description: `Component ${componentId} modified differently in both branches`,
          };
        }
      }
    }

    // Case 4: Component added with same ID in both branches
    if (!base && source && target) {
      if (!this.areComponentsEqual(source, target)) {
        return {
          id: generateId(),
          type: "component_modified",
          target: "component",
          targetId: componentId,
          pageId: source.pageId,
          baseValue: undefined,
          sourceValue: source,
          targetValue: target,
          description: `Component ${componentId} added differently in both branches`,
        };
      }
    }

    return null;
  }

  /**
   * Check if a component has been modified
   */
  private isComponentModified(base: Component, current: Component): boolean {
    // Deep comparison of component properties
    const significantProps = [
      "name",
      "type",
      "position",
      "size",
      "properties",
      "style",
    ];

    return significantProps.some((prop) => {
      const baseProp = getFieldValue(base, prop);
      const currentProp = getFieldValue(current, prop);
      return JSON.stringify(baseProp) !== JSON.stringify(currentProp);
    });
  }

  /**
   * Check compatibility between two modified components
   */
  private checkComponentCompatibility(
    base: Component,
    source: Component,
    target: Component,
  ): { compatible: boolean; conflictingField?: string } {
    const fieldsThatConflict = ["position", "size", "properties", "style"];

    for (const field of fieldsThatConflict) {
      const baseValue = getFieldValue(base, field);
      const sourceValue = getFieldValue(source, field);
      const targetValue = getFieldValue(target, field);

      const sourceChanged =
        JSON.stringify(baseValue) !== JSON.stringify(sourceValue);
      const targetChanged =
        JSON.stringify(baseValue) !== JSON.stringify(targetValue);

      if (sourceChanged && targetChanged) {
        // Both branches changed the same field
        if (JSON.stringify(sourceValue) !== JSON.stringify(targetValue)) {
          return { compatible: false, conflictingField: field };
        }
      }
    }

    return { compatible: true };
  }

  /**
   * Check if two components are functionally equal
   */
  private areComponentsEqual(comp1: Component, comp2: Component): boolean {
    const significantProps = [
      "name",
      "type",
      "position",
      "size",
      "properties",
      "style",
    ];

    return significantProps.every((prop) => {
      return (
        JSON.stringify(getFieldValue(comp1, prop)) ===
        JSON.stringify(getFieldValue(comp2, prop))
      );
    });
  }

  /**
   * Detect conflicts in page changes
   */
  private detectPageConflicts(context: ConflictContext): VersionConflict[] {
    const conflicts: VersionConflict[] = [];
    const { baseSnapshot, sourceSnapshot, targetSnapshot } = context;

    const basePages = this.buildPageMap(baseSnapshot.pages);
    const sourcePages = this.buildPageMap(sourceSnapshot.pages);
    const targetPages = this.buildPageMap(targetSnapshot.pages);

    const allPageIds = new Set([
      ...Object.keys(basePages),
      ...Object.keys(sourcePages),
      ...Object.keys(targetPages),
    ]);

    for (const pageId of allPageIds) {
      const basePage = basePages[pageId];
      const sourcePage = sourcePages[pageId];
      const targetPage = targetPages[pageId];

      const conflict = this.analyzePageConflict(
        pageId,
        basePage,
        sourcePage,
        targetPage,
      );
      if (conflict) {
        conflicts.push(conflict);
      }
    }

    return conflicts;
  }

  /**
   * Analyze potential conflict for a single page
   */
  private analyzePageConflict(
    pageId: string,
    base?: PageSnapshot,
    source?: PageSnapshot,
    target?: PageSnapshot,
  ): VersionConflict | null {
    // Similar logic to component conflict detection but for pages
    if (!source && !target && base) {
      return null; // Both deleted
    }

    if (base && !source && target && this.isPageModified(base, target)) {
      return {
        id: generateId(),
        type: "page_deleted",
        target: "page",
        targetId: pageId,
        baseValue: base,
        sourceValue: undefined,
        targetValue: target,
        description: `Page ${pageId} deleted in source but modified in target`,
      };
    }

    if (base && source && !target && this.isPageModified(base, source)) {
      return {
        id: generateId(),
        type: "page_deleted",
        target: "page",
        targetId: pageId,
        baseValue: base,
        sourceValue: source,
        targetValue: undefined,
        description: `Page ${pageId} modified in source but deleted in target`,
      };
    }

    if (base && source && target) {
      const sourceModified = this.isPageModified(base, source);
      const targetModified = this.isPageModified(base, target);

      if (sourceModified && targetModified) {
        const compatibilityResult = this.checkPageCompatibility(
          base,
          source,
          target,
        );

        if (!compatibilityResult.compatible) {
          return {
            id: generateId(),
            type: "page_modified",
            target: "page",
            targetId: pageId,
            field: compatibilityResult.conflictingField,
            baseValue: base,
            sourceValue: source,
            targetValue: target,
            description: `Page ${pageId} modified differently in both branches`,
          };
        }
      }
    }

    return null;
  }

  /**
   * Check if a page has been modified
   */
  private isPageModified(base: PageSnapshot, current: PageSnapshot): boolean {
    const significantProps = [
      "pageName",
      "canvasSettings",
      "navigation",
      "metadata",
    ];

    return significantProps.some((prop) => {
      const baseProp = getFieldValue(base, prop);
      const currentProp = getFieldValue(current, prop);
      return JSON.stringify(baseProp) !== JSON.stringify(currentProp);
    });
  }

  /**
   * Check compatibility between two modified pages
   */
  private checkPageCompatibility(
    base: PageSnapshot,
    source: PageSnapshot,
    target: PageSnapshot,
  ): { compatible: boolean; conflictingField?: string } {
    const conflictingFields = ["pageName", "navigation", "canvasSettings", "metadata"];

    for (const field of conflictingFields) {
      const baseValue = getFieldValue(base, field);
      const sourceValue = getFieldValue(source, field);
      const targetValue = getFieldValue(target, field);

      const sourceChanged =
        JSON.stringify(baseValue) !== JSON.stringify(sourceValue);
      const targetChanged =
        JSON.stringify(baseValue) !== JSON.stringify(targetValue);

      if (sourceChanged && targetChanged) {
        if (JSON.stringify(sourceValue) !== JSON.stringify(targetValue)) {
          return { compatible: false, conflictingField: field };
        }
      }
    }

    return { compatible: true };
  }

  /**
   * Detect conflicts in settings
   */
  private detectSettingsConflicts(context: ConflictContext): VersionConflict[] {
    const conflicts: VersionConflict[] = [];
    const { baseSnapshot, sourceSnapshot, targetSnapshot } = context;

    // Compare navigation settings
    if (
      this.isSettingModified(
        baseSnapshot.navigation,
        sourceSnapshot.navigation,
      ) &&
      this.isSettingModified(baseSnapshot.navigation, targetSnapshot.navigation)
    ) {
      if (
        JSON.stringify(sourceSnapshot.navigation) !==
        JSON.stringify(targetSnapshot.navigation)
      ) {
        conflicts.push({
          id: generateId(),
          type: "settings_modified",
          target: "navigation",
          targetId: "navigation",
          field: "navigation",
          baseValue: baseSnapshot.navigation,
          sourceValue: sourceSnapshot.navigation,
          targetValue: targetSnapshot.navigation,
          description:
            "Navigation settings modified differently in both branches",
        });
      }
    }

    // Compare global settings
    if (
      this.isSettingModified(baseSnapshot.globalSettings, sourceSnapshot.globalSettings) &&
      this.isSettingModified(baseSnapshot.globalSettings, targetSnapshot.globalSettings)
    ) {
      if (
        JSON.stringify(sourceSnapshot.globalSettings) !==
        JSON.stringify(targetSnapshot.globalSettings)
      ) {
        conflicts.push({
          id: generateId(),
          type: "settings_modified",
          target: "settings",
          targetId: "settings",
          field: "settings",
          baseValue: baseSnapshot.globalSettings,
          sourceValue: sourceSnapshot.globalSettings,
          targetValue: targetSnapshot.globalSettings,
          description: "Global settings modified differently in both branches",
        });
      }
    }

    return conflicts;
  }

  /**
   * Check if a setting has been modified
   */
  private isSettingModified(
    base: Record<string, unknown> | ProjectNavigationSettings | ProjectSettings,
    current: Record<string, unknown> | ProjectNavigationSettings | ProjectSettings,
  ): boolean {
    return JSON.stringify(base) !== JSON.stringify(current);
  }

  /**
   * Analyze the best resolution strategy for conflicts
   */
  private analyzeResolutionStrategy(
    conflicts: VersionConflict[],
  ): "automatic" | "manual" | "impossible" {
    if (conflicts.length === 0) {
      return "automatic";
    }

    // Check for impossible conflicts (e.g., component deleted in one, modified in another)
    const hasImpossibleConflicts = conflicts.some(
      (c) => c.type === "component_deleted" || c.type === "page_deleted",
    );

    if (hasImpossibleConflicts) {
      return "impossible";
    }

    // Check for complex conflicts that require manual resolution
    const hasComplexConflicts = conflicts.some(
      (c) =>
        c.field === "properties" ||
        c.field === "style" ||
        c.field === "navigation",
    );

    if (hasComplexConflicts) {
      return "manual";
    }

    return "manual"; // Conservative approach - require manual resolution
  }

  /**
   * Calculate confidence score for conflict resolution
   */
  private calculateConfidence(conflicts: VersionConflict[]): number {
    if (conflicts.length === 0) {
      return 100;
    }

    let totalConfidence = 0;
    const maxConflicts = 10; // Normalize against this

    // Lower confidence for more conflicts
    const conflictPenalty = Math.min(conflicts.length / maxConflicts, 1) * 50;

    // Lower confidence for complex conflict types
    const complexityPenalty =
      conflicts.reduce((penalty, conflict) => {
        if (conflict.type.includes("deleted")) return penalty + 30;
        if (conflict.field === "properties" || conflict.field === "style")
          return penalty + 20;
        return penalty + 10;
      }, 0) / conflicts.length;

    totalConfidence = 100 - conflictPenalty - complexityPenalty;

    return Math.max(0, Math.min(100, totalConfidence));
  }

  /**
   * Utility: Build component map from snapshot
   */
  private buildComponentMap(snapshot: ProjectSnapshot): {
    [id: string]: Component;
  } {
    const componentMap: { [id: string]: Component } = {};

    // Handle legacy single-page components
    if (snapshot.components && Array.isArray(snapshot.components)) {
      snapshot.components.forEach((component) => {
        componentMap[component.id] = component;
      });
    }

    // Handle multi-page components
    if (snapshot.pages && Array.isArray(snapshot.pages)) {
      snapshot.pages.forEach((page) => {
        if (page.components && Array.isArray(page.components)) {
          page.components.forEach((component) => {
            componentMap[component.id] = component;
          });
        }
      });
    }

    return componentMap;
  }

  /**
   * Utility: Build page map from pages array
   */
  private buildPageMap(pages: PageSnapshot[]): { [id: string]: PageSnapshot } {
    const pageMap: { [id: string]: PageSnapshot } = {};
    pages.forEach((page) => {
      pageMap[page.pageId] = page;
    });
    return pageMap;
  }
}

/**
 * Auto-resolution strategies for common conflict types
 */
export class ConflictResolver {
  /**
   * Attempt automatic resolution of conflicts
   */
  static autoResolve(conflicts: VersionConflict[]): VersionConflict[] {
    return conflicts.map((conflict) => {
      // Try position conflicts - use the one that moved the least
      if (
        conflict.field === "position" &&
        conflict.sourceValue &&
        conflict.targetValue
      ) {
        const sourceDistance = this.calculatePositionDistance(
          conflict.baseValue,
          conflict.sourceValue,
        );
        const targetDistance = this.calculatePositionDistance(
          conflict.baseValue,
          conflict.targetValue,
        );

        return {
          ...conflict,
          resolution:
            sourceDistance < targetDistance ? "use_source" : "use_target",
          resolvedBy: "auto-resolver",
          resolvedAt: new Date(),
        };
      }

      // Try size conflicts - use the larger size
      if (
        conflict.field === "size" &&
        conflict.sourceValue &&
        conflict.targetValue
      ) {
        const sourceSize = conflict.sourceValue as Record<string, unknown>;
        const targetSize = conflict.targetValue as Record<string, unknown>;

        const sourceArea = (Number(sourceSize.width) || 0) * (Number(sourceSize.height) || 0);
        const targetArea = (Number(targetSize.width) || 0) * (Number(targetSize.height) || 0);

        return {
          ...conflict,
          resolution: sourceArea > targetArea ? "use_source" : "use_target",
          resolvedBy: "auto-resolver",
          resolvedAt: new Date(),
        };
      }

      // For other conflicts, require manual resolution
      return conflict;
    });
  }

  /**
   * Calculate distance between two positions
   */
  private static calculatePositionDistance(
    base: unknown,
    current: unknown,
  ): number {
    if (!base || !current || typeof base !== 'object' || typeof current !== 'object') return Infinity;

    const basePos = base as Record<string, unknown>;
    const currentPos = current as Record<string, unknown>;

    const dx = (Number(basePos.x) || 0) - (Number(currentPos.x) || 0);
    const dy = (Number(basePos.y) || 0) - (Number(currentPos.y) || 0);

    return Math.sqrt(dx * dx + dy * dy);
  }
}

// Export the main conflict detection function
export const detectVersionConflicts = async (
  context: ConflictContext,
): Promise<ConflictDetectionResult> => {
  const detector = ConflictDetector.getInstance();
  return detector.detectConflicts(context);
};
